import * as React from "react";
import { FC, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../actions/navigation";
import { RestrictedLayout } from "../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../constants/fontAwesomeIcons";
import { ITmdState } from "../../reducers";

interface IErrorType {
    code: string;
    title: string;
    message: string;
}

interface ErrorPageProps {
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const ErrorTypes = {
    400: {
        code: "400",
        title: "Bad Request",
        message:
            "Can't process a request due to formatting issues or missing information.",
    } as IErrorType,
    401: {
        code: "401",
        title: "Unauthorized",
        message:
            "The current user lacks valid authentication credentials for the requested resource.",
    } as IErrorType,
    403: {
        code: "403",
        title: "Forbidden",
        message:
            "The current user has insufficient permissions to access the requested resource.",
    } as IErrorType,
    404: {
        code: "404",
        title: "Page Not Found",
        message:
            "The requested resource could not be found. Please check the requested URL is correct.",
    } as IErrorType,
    500: {
        code: "500",
        title: "Internal Server Error",
        message:
            "An error occurred on the server while attempting to process the request, please try again.",
    } as IErrorType,
    generic: {
        code: "500",
        title: "Internal Server Error",
        message:
            "An error occurred on the server while attempting to process the request, please try again.",
    } as IErrorType,
};

const ErrorPage: FC<ErrorPageProps> = ({ setBreadcrumbs, setDrawerItems }) => {
    //
    // -- Params --
    const { errorCode } = useParams<{ errorCode: string }>();
    const errorCodeNumber = parseInt(errorCode, 10);


    useEffect(() => {
        setDrawerItems([]);
        setBreadcrumbs([]);
    }, [setDrawerItems, setBreadcrumbs]);

    const renderContent = useMemo(() => {
        let errorType = ErrorTypes.generic;

        if (errorCode && ErrorTypes[errorCodeNumber]) {
            errorType = ErrorTypes[errorCodeNumber];
        }

        return (
            <div className="container-fluid">
                <div className="container">
                    <div className="row">
                        <div className="col">
                            <div className="jumbotron my-5 text-center">
                                <div className="text-danger mb-7">
                                    <FontAwesomeIcon
                                        icon={
                                            FontAwesomeIcons.Solid
                                                .EXCLAMATION_TRIANGLE
                                        }
                                        fixedWidth
                                        transform="grow-64"
                                    />
                                </div>
                                <div className="display-3 text-danger">
                                    {errorType.code}
                                </div>
                                <p className="lead">{errorType.title}</p>
                                <hr className="my-4" />
                                <p>{errorType.message}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, []);

    return (
        <RestrictedLayout subMenuItems={undefined}>
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (_state: ITmdState): Partial<ErrorPageProps> => {
    return {};
};

const mapDispatchToProps = (dispatch): Partial<ErrorPageProps> => {
    return {
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ErrorPage);
